import React from 'react';
import {
  Alert, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import { saveAppointmentTimeZone, subscribeToAppointments, updateAppointmentStatus } from '../../services/appointmentScheduleService';
import {
  formatAppointmentDateTime, formatAppointmentDay, groupAppointmentsByDay, isPastAppointment,
} from '../../features/appointments/appointmentPresentation';
import AppointmentEditorSheet from '../../features/appointments/AppointmentEditorSheet';

const MANAGER_ROLES = new Set(['care_owner', 'care_partner']);

const getDeviceTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const COMMON_TIME_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC',
];

const TimeZoneDialog = ({ childId, onClose, onSaved }) => {
  const deviceTimeZone = getDeviceTimeZone();
  const supportedTimeZones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const timeZones = [...new Set([deviceTimeZone, ...COMMON_TIME_ZONES, ...supportedTimeZones])];
  const [timeZone, setTimeZone] = React.useState(deviceTimeZone);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await saveAppointmentTimeZone(childId, timeZone);
      onSaved(timeZone);
    } catch {
      setSaving(false);
      setError('Timezone could not be saved. Try again.');
    }
  };
  return <Dialog open onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle>Set profile timezone</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      <Typography color="text.secondary">Appointment times and reminders will use this timezone for this profile.</Typography>
      {error ? <Alert severity="warning">{error}</Alert> : null}
      <TextField select SelectProps={{ native: true }} label="Profile timezone" value={timeZone} onChange={(event) => setTimeZone(event.target.value)} inputProps={{ 'aria-label': 'Profile timezone' }}>
        {timeZones.map((value) => <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>)}
      </TextField>
    </Stack></DialogContent>
    <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Confirm timezone'}</Button></DialogActions>
  </Dialog>;
};

const AppointmentDetails = ({ appointment, canManage, onEdit, onStatus, onClose }) => {
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  return <>
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{appointment.title}</DialogTitle>
      <DialogContent><Stack spacing={1}>
        <Typography>{formatAppointmentDateTime(appointment)}</Typography>
        {appointment.provider ? <Typography color="text.secondary">{appointment.provider}</Typography> : null}
        <Typography variant="body2" color="text.secondary">{appointment.reminderRules?.length ? 'Care team reminder on' : 'No reminder'}</Typography>
      </Stack></DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={onClose}>Close</Button>
        {canManage && appointment.status === 'confirmed' ? <Button onClick={onEdit}>Edit</Button> : null}
        {canManage && appointment.status === 'confirmed' ? <Button onClick={() => onStatus('completed')}>Mark complete</Button> : null}
        {canManage && appointment.status === 'confirmed' ? <Button color="error" onClick={() => setConfirmCancel(true)}>Cancel appointment</Button> : null}
      </DialogActions>
    </Dialog>
    <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
      <DialogTitle>Cancel this appointment?</DialogTitle>
      <DialogContent><Typography>The reminder will also be cancelled.</Typography></DialogContent>
      <DialogActions><Button onClick={() => setConfirmCancel(false)}>Keep appointment</Button><Button color="error" onClick={() => onStatus('cancelled')}>Yes, cancel appointment</Button></DialogActions>
    </Dialog>
  </>;
};

const AppointmentList = ({ title, appointments, onOpen }) => !appointments.length ? null : (
  <Box component="section" sx={{ mt: 3 }}>
    <Typography component="h2" variant="overline" sx={{ fontWeight: 900 }}>{title}</Typography>
    {Object.entries(groupAppointmentsByDay(appointments)).map(([dayKey, dayAppointments]) => (
      <Box key={dayKey} sx={{ mt: 1.5 }}><Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.6, fontWeight: 800 }}>{formatAppointmentDay(dayKey)}</Typography>
        <Stack divider={<Divider flexItem />} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
          {dayAppointments.map((appointment) => <ButtonBase key={appointment.id} aria-label={`${appointment.title}, ${formatAppointmentDateTime(appointment)}`} onClick={() => onOpen(appointment)} sx={{ p: 2, textAlign: 'left', justifyContent: 'flex-start' }}>
            <Box><Typography sx={{ fontWeight: 900 }}>{appointment.title}</Typography><Typography variant="body2" color="text.secondary">{formatAppointmentDateTime(appointment)}{appointment.status !== 'confirmed' ? ` · ${appointment.status}` : ''}</Typography></Box>
          </ButtonBase>)}
        </Stack>
      </Box>
    ))}
  </Box>
);

const AppointmentsPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { childrenWithAccess = [], getUserRoleForChild, loading: roleLoading } = useRole();
  const child = childrenWithAccess.find((item) => item.id === childId) || null;
  const canManage = MANAGER_ROLES.has(getUserRoleForChild?.(childId));
  const [appointments, setAppointments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [editing, setEditing] = React.useState(undefined);
  const [selected, setSelected] = React.useState(null);
  const [completedAppointment, setCompletedAppointment] = React.useState(null);
  const [notice, setNotice] = React.useState('');
  const [profileTimeZone, setProfileTimeZone] = React.useState(child?.timeZone || child?.timezone || '');
  const [settingTimeZone, setSettingTimeZone] = React.useState(false);
  const openedLinkedAppointmentIdRef = React.useRef('');
  const appointmentDraft = location.state?.appointmentDraft || null;
  React.useEffect(() => {
    setProfileTimeZone(child?.timeZone || child?.timezone || '');
  }, [child?.timeZone, child?.timezone]);
  React.useEffect(() => {
    if (appointmentDraft && profileTimeZone && canManage) setEditing((current) => current === undefined ? appointmentDraft : current);
  }, [appointmentDraft, canManage, profileTimeZone]);
  React.useEffect(() => {
    if (roleLoading || !child) return undefined;
    return subscribeToAppointments(childId, (items) => { setAppointments(items); setLoading(false); }, () => { setError('Appointments could not be loaded.'); setLoading(false); });
  }, [child, childId, roleLoading]);
  React.useEffect(() => {
    const linkedId = new URLSearchParams(location.search).get('appointmentId');
    if (linkedId && openedLinkedAppointmentIdRef.current !== linkedId) {
      const linkedAppointment = appointments.find((item) => item.id === linkedId);
      if (linkedAppointment) {
        openedLinkedAppointmentIdRef.current = linkedId;
        setSelected(linkedAppointment);
      }
    }
  }, [appointments, location.search]);
  if (roleLoading || (loading && child)) return <Box sx={{ minHeight: '70dvh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!roleLoading && !child) return <Navigate to="/home" replace />;
  const timeZone = profileTimeZone;
  const upcoming = appointments.filter((item) => !isPastAppointment(item));
  const past = appointments.filter((item) => isPastAppointment(item)).sort((a, b) => `${b.startDate}T${b.startTime}`.localeCompare(`${a.startDate}T${a.startTime}`)).slice(0, 10);
  const closeForm = (saved) => { setEditing(undefined); if (saved) setNotice('Appointment saved.'); };
  const changeStatus = async (status) => {
    try {
      await updateAppointmentStatus(selected.id, status);
      if (status === 'completed') setCompletedAppointment(selected);
      setSelected(null);
      setNotice(status === 'completed' ? 'Appointment completed.' : 'Appointment cancelled.');
    } catch { setNotice('Appointment could not be updated. Try again.'); }
  };
  const addTimelineNote = () => navigate('/dashboard', { state: {
    selectedChildId: childId,
    openQuickEntry: true,
    quickEntryInitialText: completedAppointment
      ? `Appointment note: ${completedAppointment.title}${completedAppointment.provider ? ` with ${completedAppointment.provider}` : ''}`
      : 'Appointment note:',
  } });
  return <Box sx={{ minHeight: '100dvh', bgcolor: '#F8FAF9', pb: 5 }}><Box sx={{ maxWidth: 680, mx: 'auto', px: { xs: 2, sm: 3 }, pt: 2 }}>
    <Button onClick={() => navigate('/home')} startIcon={<ArrowBackRoundedIcon />} sx={{ px: 0, textTransform: 'none', fontWeight: 800 }}>Back to Home</Button>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mt: 2 }}><Box><Typography variant="overline" color="text.secondary" sx={{ fontWeight: 900 }}>{child.name}'s care</Typography><Typography component="h1" variant="h4" sx={{ fontWeight: 900 }}>{child.name}'s appointments</Typography></Box>{canManage && timeZone && appointments.length ? <Button variant="contained" onClick={() => setEditing(null)}>Add appointment</Button> : null}</Stack>
    {error ? <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert> : null}
    {!timeZone ? <Alert severity="warning" action={canManage ? <Button color="inherit" onClick={() => setSettingTimeZone(true)}>Set timezone</Button> : null} sx={{ mt: 2 }}>Set this profile's timezone before adding an appointment.</Alert> : null}
    {!error && !appointments.length ? <Stack alignItems="center" textAlign="center" spacing={1.5} sx={{ mt: 6, p: 4 }}><EventRoundedIcon color="primary" sx={{ fontSize: 38 }} /><Typography variant="h6" sx={{ fontWeight: 900 }}>No appointments yet</Typography><Typography color="text.secondary">Keep upcoming care in one place and remind the care team before it happens.</Typography>{canManage && timeZone ? <Button variant="contained" onClick={() => setEditing(null)}>Add appointment</Button> : null}</Stack> : null}
    <AppointmentList title="Coming up" appointments={upcoming} onOpen={setSelected} />
    <AppointmentList title="Past" appointments={past} onOpen={setSelected} />
  </Box>
  {editing !== undefined ? <AppointmentEditorSheet child={child} timeZone={timeZone} appointment={editing} onClose={closeForm} /> : null}
  {settingTimeZone ? <TimeZoneDialog childId={childId} onClose={() => setSettingTimeZone(false)} onSaved={(savedTimeZone) => { setProfileTimeZone(savedTimeZone); setSettingTimeZone(false); setEditing(appointmentDraft || null); setNotice('Timezone saved.'); }} /> : null}
  {selected ? <AppointmentDetails appointment={selected} canManage={canManage} onClose={() => setSelected(null)} onEdit={() => { setEditing(selected); setSelected(null); }} onStatus={changeStatus} /> : null}
  <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')} message={notice} action={notice === 'Appointment completed.' ? <Button color="inherit" onClick={addTimelineNote}>Add appointment note</Button> : null} />
  </Box>;
};

export default AppointmentsPage;
